import {
  createQueryKeyStore,
  inferQueryKeyStore,
} from "@lukemorales/query-key-factory"
import {
  getAccountBillRequests,
  getAccountCommunicationEmail,
  getAccountCommunicationFile,
  getAccountCommunications,
  getAccountDetailsByNumber,
} from "./generated/account/account"
import { getAccountsByProperty } from "./generated/property-accounts/property-accounts"
import { getAccountContacts } from "./generated/account-contact/account-contact"
import { getAccountNotes } from "./generated/account-notes/account-notes"
import { getAccountRtsStatus } from "./generated/account-rts/account-rts"
import { getAccountUnderReviewPeriods } from "./generated/account-under-review-periods/account-under-review-periods"
import {
  getPaperBillState,
  getPsrCustomerSettings,
  getPsrServicesByVulnerabilityIds,
  getPsrVulnerabilityCategories,
} from "./generated/psr/psr"
import { getAccountTickets } from "./generated/account-tickets/account-tickets"
import { getAccountTransactions } from "./generated/account-transactions/account-transactions"
import {
  getAccountBill,
  getAccountBillBreakdown,
  getAccountBillEmail,
  getAccountBillImage,
  getAccountBills,
  getBillsZipLinkByBillingAccountId,
  getUnbilledReadingCostsByBillingAccountId,
} from "./generated/billing/billing"
import { getCommunicationAttachmentSignedUrl } from "./generated/communication/communication"
import { searchMandrill } from "./generated/mandrill/mandrill"
import { listSmsBatches } from "./generated/sms-batch/sms-batch"
import {
  checkSmartPayAsYouGoEligibility,
  getSmartPayAsYouGoExecutionDate,
} from "./generated/smart-pay-as-you-go/smart-pay-as-you-go"
import { getRepaymentAmount } from "./generated/payg/payg"
import { getEnergyUsageV2 } from "./generated/energy-usage/energy-usage"
import {
  getMeterPointAgreements,
  getMeterpointConsents,
  getTariffAgreements,
  searchMpan,
  searchMprn,
} from "./generated/meterpoints/meterpoints"
import {
  getAccountMeterpoints,
  getAccountMeterpointsStructure,
} from "./generated/accounts-meterpoints/accounts-meterpoints"
import {
  getAppointmentHistory,
  getEligibilityReason,
  getIHDs,
  getOfferedAppointments,
  getSmartMeterInterest,
  getSyncedDateTime,
} from "./generated/smart-meters/smart-meters"
import {
  generateMeterReadingPhotoEvidenceGetUrl,
  getMeterReadingPhotoEvidenceList,
  getMeterReadingPhotoEvidenceUploadUrlsList,
} from "./generated/meter-reading/meter-reading"
import { getTariffMonthlyRates } from "./generated/tariff/tariff"
import { EnergyUsageGranularity } from "./generated/model"
import { getAlertBannersDefinitions } from "./generated/alerts/alerts"
import { hasOpenComplaintOnAccount } from "./generated/complaint/complaint"
import { getDcaAllocationConfig } from "./generated/dca-allocation-config/dca-allocation-config"
import { getDcaLitigationFileSummary } from "./generated/dca-litigation-file-summary/dca-litigation-file-summary"
import { fetchSuggestedPayment } from "./generated/direct-debit/direct-debit"
import { getAccountDirectDebitMandates } from "./generated/account-direct-debit-mandates/account-direct-debit-mandates"
import { getPaymentScheduleDetails } from "./generated/payments/payments"
import {
  getPaymentPlansByAccountId,
  getPaymentPlanOptionsByAccountId,
} from "./generated/payment-plans/payment-plans"
import {
  listCommunications,
  listThrottleConfigs,
  listThrottleEvents,
} from "./generated/email-throttle/email-throttle"
import {
  listFeatureFlagChanges,
  listFeatureFlags,
} from "./generated/feature-flags/feature-flags"
import {
  getActiveDrawInfoForAccount,
  getDrawDetail,
  getDrawWinners,
  listDraws,
} from "./generated/loyalty/loyalty"
import {
  getMeterExchangeValidity,
  getWarrantStatus,
} from "./generated/warrants/warrants"

const RECENT_CHANGES_PER_FLAG = 5

export const BackofficeRestQueries = createQueryKeyStore({
  account: {
    detailsByAccountNumber: (accountNumber: string) => ({
      queryKey: [accountNumber],
      queryFn: () => getAccountDetailsByNumber({ accountNumber }),
    }),
    rtsStatus: (accountId: number) => ({
      queryKey: [accountId],
      queryFn: () => getAccountRtsStatus(accountId),
    }),
    unbilledReadingCosts: (billingAccountId: number) => ({
      queryKey: [billingAccountId],
      queryFn: () =>
        getUnbilledReadingCostsByBillingAccountId(billingAccountId),
    }),
    suggestedPayments: (billingAccountId: number) => ({
      queryKey: [billingAccountId],
      queryFn: () => fetchSuggestedPayment(billingAccountId),
    }),
    getActiveDrawInfoForAccount: (billingAccountId: number) => ({
      queryKey: [billingAccountId],
      queryFn: () => getActiveDrawInfoForAccount(billingAccountId),
    }),
    freshdeskAttachmentSignedUrl: (path: string) => ({
      queryKey: [path],
      queryFn: () => getCommunicationAttachmentSignedUrl({ path }),
    }),
    energyUsage: (
      accountId: number,
      fromDate: string,
      toDate: string,
      granularity: EnergyUsageGranularity,
    ) => ({
      queryKey: [accountId, fromDate, toDate, granularity],
      queryFn: () =>
        getEnergyUsageV2(accountId, { fromDate, toDate, granularity }),
    }),
    tariffMonthlyRates: (productCode: string, gspGroup: string) => ({
      queryKey: [productCode, gspGroup],
      queryFn: () => getTariffMonthlyRates({ productCode, gspGroup }),
    }),
    contacts: (billingAccountId: number) => ({
      queryKey: [billingAccountId],
      queryFn: () => getAccountContacts(billingAccountId),
    }),
    accountNotes: (billingAccountId: number) => ({
      queryKey: [billingAccountId],
      queryFn: () => getAccountNotes(billingAccountId),
    }),
    juniferTickets: (billingAccountId: number) => ({
      queryKey: [billingAccountId],
      queryFn: () => getAccountTickets(billingAccountId),
    }),
    juniferComms: (billingAccountId: number) => ({
      queryKey: [billingAccountId],
      queryFn: () => getAccountCommunications(billingAccountId),
    }),
    juniferCommEmail: (billingAccountId: number, communicationId: number) => ({
      queryKey: [billingAccountId, communicationId],
      queryFn: () =>
        getAccountCommunicationEmail(billingAccountId, communicationId),
    }),
    juniferCommFile: (
      billingAccountId: number,
      communicationId: number,
      fileId: number,
    ) => ({
      queryKey: [billingAccountId, communicationId, fileId],
      queryFn: () =>
        getAccountCommunicationFile(billingAccountId, communicationId, fileId),
    }),
  },
  smartMeter: {
    syncedDateTime: () => ({
      queryKey: ["smartMeterSyncedDateTime"],
      queryFn: () => getSyncedDateTime(),
    }),
    getIhdDetails: (mpxns: string) => ({
      queryKey: [JSON.stringify(mpxns)],
      queryFn: () => getIHDs(mpxns),
    }),
    getRegisteredInterest: (billingAccountId: string) => ({
      queryKey: [billingAccountId],
      queryFn: () => getSmartMeterInterest(billingAccountId),
    }),
    getPreviouslyOfferedSlots: (billingAccountId: string) => ({
      queryKey: [billingAccountId],
      queryFn: () => getOfferedAppointments(billingAccountId),
    }),
    getSmartMeterEligibility: (accountNumber: string) => ({
      queryKey: [accountNumber],
      queryFn: () => getEligibilityReason(accountNumber),
    }),
  },
  meterAppointment: {
    getAppointment: (billingAccountId: number) => ({
      queryKey: [billingAccountId],
      queryFn: () => getAppointmentHistory(billingAccountId.toString()),
    }),
  },
  meterReadings: {
    photoEvidence: (accountNumber: string) => ({
      queryKey: ["photoEvidence", accountNumber],
      queryFn: () => getMeterReadingPhotoEvidenceList({ accountNumber }),
    }),
    photoUrl: (imageIdentifier: string) => ({
      queryKey: ["imageIdentifier", imageIdentifier],
      queryFn: () =>
        generateMeterReadingPhotoEvidenceGetUrl({ imageIdentifier }),
    }),
    getMeterReadingPhotoEvidenceUploadUrlsList: (
      imageIdentifiers: string[],
    ) => ({
      queryKey: [imageIdentifiers],
      queryFn: () =>
        getMeterReadingPhotoEvidenceUploadUrlsList({ imageIdentifiers }),
    }),
  },
  meterpoints: {
    forAccount: (accountNumber: string) => ({
      queryKey: [accountNumber],
      queryFn: () => getAccountMeterpointsStructure(accountNumber),
    }),
    forCoreAccount: (accountId: number) => ({
      queryKey: [accountId],
      queryFn: () => getAccountMeterpoints(accountId),
    }),
    consents: (accountId: number) => ({
      queryKey: [accountId],
      queryFn: () => getMeterpointConsents(accountId),
    }),
    searchMpan: (postcode: string) => ({
      queryKey: [postcode],
      queryFn: () => searchMpan({ postcode }),
    }),
    searchMprn: (postcode: string) => ({
      queryKey: [postcode],
      queryFn: () => searchMprn({ postcode }),
    }),
  },
  meterpointAgreements: {
    getMeterPointAgreements: (
      billingAccountId: number,
      meterPointIdentifier: string,
    ) => ({
      queryKey: [billingAccountId, meterPointIdentifier],
      queryFn: () =>
        getMeterPointAgreements(meterPointIdentifier, { billingAccountId }),
    }),
    getTariffAgreements: (
      billingAccountId: number,
      meterPointIdentifier: string,
    ) => ({
      queryKey: [billingAccountId, meterPointIdentifier],
      queryFn: () =>
        getTariffAgreements(meterPointIdentifier, { billingAccountId }),
    }),
  },
  smartPayAsYouGo: {
    checkEligibility: (accountNumber: string) => ({
      queryKey: [accountNumber],
      queryFn: () =>
        checkSmartPayAsYouGoEligibility({
          billingAccountNumber: accountNumber,
        }),
    }),
    getExecutionDate: (accountNumber: string) => ({
      queryKey: [accountNumber],
      queryFn: () =>
        getSmartPayAsYouGoExecutionDate({
          billingAccountNumber: accountNumber,
        }),
    }),
    getRepaymentAmount: {
      queryKey: null,
      queryFn: () => getRepaymentAmount(),
    },
  },
  smsBatches: {
    list: () => ({
      queryKey: ["smsBatchesList"],
      queryFn: () => listSmsBatches(),
    }),
  },
  communication: {
    mandrillSearch: (query: string, dateFrom: string, dateTo: string) => ({
      queryKey: [query, dateFrom, dateTo],
      queryFn: () => searchMandrill({ query, dateFrom, dateTo }),
    }),
  },
  propertyAccounts: {
    // All accounts (current and ceased) that have held a given Junifer property.
    atProperty: (propertyId: number) => ({
      queryKey: [propertyId],
      queryFn: () => getAccountsByProperty(propertyId),
    }),
  },
  payments: {
    // GraphQL keyed this by the stringified id; keep it so existing
    // cache-invalidation keys still match.
    scheduleDetails: (billingAccountId: number) => ({
      queryKey: [billingAccountId.toString()],
      queryFn: () => getPaymentScheduleDetails(billingAccountId),
    }),
    directDebitMandates: (billingAccountId: number) => ({
      queryKey: [billingAccountId],
      queryFn: () => getAccountDirectDebitMandates(billingAccountId),
    }),
  },
  billing: {
    bills: (billingAccountId: number) => ({
      queryKey: [billingAccountId],
      queryFn: () => getAccountBills(billingAccountId),
    }),
    bill: (billId: number, billingAccountId: number) => ({
      queryKey: [billId, billingAccountId],
      queryFn: () => getAccountBill(billingAccountId, billId),
    }),
    billBreakdown: (billId: number) => ({
      queryKey: [billId],
      queryFn: () => getAccountBillBreakdown(billId),
    }),
    billEmail: (billId: number) => ({
      queryKey: [billId],
      queryFn: () => getAccountBillEmail(billId),
    }),
    billImage: (billId: number) => ({
      queryKey: [billId],
      queryFn: () => getAccountBillImage(billId),
    }),
    billsZipLink: (billingAccountId: number) => ({
      queryKey: [billingAccountId],
      queryFn: () => getBillsZipLinkByBillingAccountId(billingAccountId),
    }),
    transactions: (billingAccountId: number) => ({
      queryKey: [billingAccountId],
      queryFn: () => getAccountTransactions(billingAccountId),
    }),
    billRequests: (billingAccountId: number) => ({
      queryKey: [billingAccountId],
      queryFn: () => getAccountBillRequests(billingAccountId),
    }),
  },
  alertsBanners: {
    definitions: {
      queryKey: null,
      queryFn: () => getAlertBannersDefinitions(),
    },
  },
  throttle: {
    configs: {
      queryKey: null,
      queryFn: () => listThrottleConfigs(),
    },
    events: (status?: string) => ({
      queryKey: [status ?? "all"],
      queryFn: () => listThrottleEvents(status ? { status } : undefined),
    }),
    communications: {
      queryKey: null,
      queryFn: () => listCommunications(),
    },
  },
  warrants: {
    status: (billingAccountId: number) => ({
      queryKey: [billingAccountId],
      queryFn: () => getWarrantStatus(billingAccountId),
    }),
    meterExchangeEligibility: (billingAccountId: number) => ({
      queryKey: [billingAccountId],
      queryFn: () => getMeterExchangeValidity(billingAccountId),
    }),
  },
  paymentPlans: {
    list: (accountId: number) => ({
      queryKey: [accountId],
      queryFn: () => getPaymentPlansByAccountId(accountId),
    }),
    options: (accountId: number, billingAccountId?: number) => ({
      queryKey: [accountId, billingAccountId],
      queryFn: () =>
        getPaymentPlanOptionsByAccountId(
          accountId,
          billingAccountId ? { billingAccountId } : undefined,
        ),
    }),
  },
  reviewPeriods: {
    list: (billingAccountId: number) => ({
      queryKey: [billingAccountId],
      queryFn: () => getAccountUnderReviewPeriods(billingAccountId),
    }),
  },
  paperBill: {
    state: (billingAccountId: number) => ({
      queryKey: [billingAccountId],
      queryFn: () => getPaperBillState(billingAccountId),
    }),
  },
  psr: {
    customerSettings: (customerId: number) => ({
      queryKey: [customerId],
      queryFn: () => getPsrCustomerSettings(customerId),
    }),
    categories: {
      queryKey: null,
      queryFn: () => getPsrVulnerabilityCategories(),
    },
    servicesByVulnerabilityIds: (ids: string) => ({
      queryKey: [ids],
      queryFn: () => getPsrServicesByVulnerabilityIds({ ids }),
    }),
  },
  dcaAllocation: {
    config: {
      queryKey: null,
      queryFn: () => getDcaAllocationConfig(),
    },
    litigationFileSummary: {
      queryKey: null,
      queryFn: () => getDcaLitigationFileSummary(),
    },
  },
  featureFlags: {
    list: () => ({
      queryKey: ["featureFlags", "list"],
      queryFn: () => listFeatureFlags(),
    }),
    // featureId null lists changes across all flags. limit is per flag, so keep that view to each
    // flag's few latest changes; one flag's history uses the server default.
    changes: (featureId: number | null) => ({
      queryKey: ["featureFlags", "changes", featureId ?? "all"],
      queryFn: () =>
        listFeatureFlagChanges(
          featureId === null
            ? { limit: RECENT_CHANGES_PER_FLAG }
            : { featureId },
        ),
    }),
  },
  complaint: {
    hasOpen: (accountId: number) => ({
      queryKey: [accountId],
      queryFn: () => hasOpenComplaintOnAccount({ accountId }),
    }),
  },
  loyalty: {
    listDraws: () => ({
      queryKey: ["listDraws"],
      queryFn: () => listDraws(),
    }),
    getDrawDetail: (drawId: string) => ({
      queryKey: [drawId],
      queryFn: () => getDrawDetail(Number(drawId)),
    }),
    getDrawWinners: (drawId: string) => ({
      queryKey: [drawId],
      queryFn: () => getDrawWinners(Number(drawId)),
    }),
    // Status is a field of the draw detail; reuse that endpoint under a distinct
    // key so its short polling cache stays separate from the full detail read.
    getDrawStatus: (drawId: string) => ({
      queryKey: [drawId],
      queryFn: () => getDrawDetail(Number(drawId)),
    }),
  },
})

export type BackofficeRestQueryKeys = inferQueryKeyStore<
  typeof BackofficeRestQueries
>
